/**
 * Executive Intelligence Dossier Types
 *
 * Types for the comprehensive PE due diligence dossier view.
 */

// Re-export common types
export type VerificationStatus = 'verified' | 'corroborated' | 'unverified' | 'stale';
export type ExecutiveSourceType = 'company_website' | 'sec_filing' | 'linkedin' | 'news_article' | 'press_release' | 'brave_search' | 'manual';
export type Confidence = 'high' | 'medium' | 'low';

// Traffic Light Assessment Dimensions
export type DimensionRating = 'green' | 'yellow' | 'red' | 'insufficient';

export type ExecutiveDimension =
  | 'drive_ambition'
  | 'resilience'
  | 'people_culture'
  | 'risk_discipline'
  | 'intellectual_range'
  | 'derailer_flags'
  | 'motivations';

export interface DimensionAssessment {
  dimension: ExecutiveDimension;
  rating: DimensionRating;
  rationale: string;
  evidence: string[];
  confidence: Confidence;
}

export interface KeyFinding {
  finding_type: 'strength' | 'watch_item' | 'flag';
  title: string;
  description: string;
  source_ids: string[];
}

export interface ExecutiveAssessment {
  executive_id: string;
  dimensions: DimensionAssessment[];
  key_findings: KeyFinding[];
  intelligence_thesis: string;
  data_gaps: string[];
  generated_at: string;
}

// Bio fragment from sources
export interface BioFragment {
  source_type: string;
  source_url?: string;
  text: string;
  extracted_at: string;
  is_authoritative: boolean;
}

// Contact information
export interface ContactInfo {
  contact_type: string;
  value: string;
  is_verified: boolean;
  confidence?: Confidence;
  notes?: string;
}

// Career position
export interface CareerPosition {
  company_name: string;
  title: string;
  start_date?: string;
  end_date?: string;
  is_current: boolean;
  responsibilities?: string;
  achievements?: string[];
}

// Board membership
export interface BoardMembership {
  organization_name: string;
  role: string;
  is_corporate: boolean;
  start_date?: string;
  end_date?: string;
  is_current: boolean;
}

// Education
export interface Education {
  institution: string;
  degree?: string;
  field_of_study?: string;
  graduation_year?: number;
  honors?: string;
}

// Legal record (for Phase 3)
export interface LegalRecord {
  id: string;
  record_type: 'litigation' | 'enforcement' | 'sanctions';
  parties: string[];
  case_number?: string;
  court_or_agency: string;
  filing_date: string;
  status: string;
  description: string;
  source_url: string;
}

// Sanctions screening result
export interface SanctionsScreenResult {
  name_searched: string;
  match_found: boolean;
  match_confidence: number;
  list_name?: string;
  listing_details?: string;
  screened_at: string;
}

// Insider transaction (for Phase 4)
export interface InsiderTransaction {
  id: string;
  transaction_date: string;
  transaction_type: string;
  shares: number;
  price_per_share: number;
  total_value: number;
  filing_date: string;
  source_url: string;
}

// Full executive detail for dossier
export interface ExecutiveDetail {
  id: string;
  full_name: string;
  first_name?: string;
  last_name?: string;
  preferred_name?: string;
  current_title?: string;
  current_company_id?: string;
  current_company_name?: string;
  current_role_category?: string;
  photo_url?: string;

  // Verification
  verification_status?: VerificationStatus;
  verification_sources?: string[];
  verification_notes?: string;
  source_types?: ExecutiveSourceType[];

  // Bio
  bio?: {
    synthesized_bio?: string;
    fragments?: BioFragment[];
    key_facts?: string[];
    career_highlights?: string[];
  };

  // Professional history
  contacts?: ContactInfo[];
  career_history?: CareerPosition[];
  board_memberships?: BoardMembership[];
  education?: Education[];

  // Financial (Phase 4)
  insider_transactions?: InsiderTransaction[];
  equity_holdings?: {
    value: number;
    unit: string;
    as_of_date: string;
    is_estimated: boolean;
  };

  // Legal (Phase 3)
  legal_records?: LegalRecord[];
  sanctions_screen?: SanctionsScreenResult;

  // Assessment (Phase 2)
  assessment?: ExecutiveAssessment;

  // Metadata
  data_completeness?: number;
  created_at?: string;
  updated_at?: string;
  last_verified?: string;
}

// Dimension metadata for display
export const DIMENSION_LABELS: Record<ExecutiveDimension, { label: string; description: string }> = {
  drive_ambition: {
    label: 'Drive & Ambition',
    description: 'Career progression, promotions, competitive compensation',
  },
  resilience: {
    label: 'Resilience Under Pressure',
    description: 'Crisis leadership, performance during downturns',
  },
  people_culture: {
    label: 'People & Culture Signals',
    description: 'Glassdoor themes, team retention, leadership style',
  },
  risk_discipline: {
    label: 'Risk Discipline',
    description: 'Regulatory record, enforcement history, compliance',
  },
  intellectual_range: {
    label: 'Intellectual Range',
    description: 'Patents, publications, board diversity, outside engagement',
  },
  derailer_flags: {
    label: 'Derailer Flags',
    description: 'Litigation patterns, ethical concerns, conflicts',
  },
  motivations: {
    label: 'What Motivates Them',
    description: 'Alignment with deal thesis, incentive structure',
  },
};

// Rating colors for traffic light
export const RATING_COLORS: Record<DimensionRating, { bg: string; text: string; icon: string }> = {
  green: { bg: 'bg-green-100', text: 'text-green-800', icon: '🟢' },
  yellow: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: '🟡' },
  red: { bg: 'bg-red-100', text: 'text-red-800', icon: '🔴' },
  insufficient: { bg: 'bg-gray-100', text: 'text-gray-600', icon: '⚪' },
};

// Finding type colors
export const FINDING_COLORS: Record<KeyFinding['finding_type'], { bg: string; border: string; text: string; icon: string }> = {
  strength: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', icon: '✓' },
  watch_item: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800', icon: '!' },
  flag: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: '⚠' },
};
